import React, { Component } from 'react';
import './Game.css';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo/index';
import gql from 'graphql-tag';
import { tournamentShape } from 'utils/shapes';
import { setLastGame } from 'utils/localStorage';
import { getToken } from 'utils/auth';

class Game extends Component {
  heartBeatInterval = null;

  constructor(props) {
    super(props);

    this.state = {
      gameSession: undefined,
      sessionStarted: false,
      loading: false,
      error: undefined,
      startError: undefined
    };

    this.handleUnload = this.handleUnload.bind(this);
    this.sendHeartBeat = this.sendHeartBeat.bind(this);
  }

  sendHeartBeat() {
    this.props.sendGameSessionHeartbeat &&
      this.props.sendGameSessionHeartbeat(this.state.gameSession);
  }

  componentDidMount() {
    this.setState({ loading: true });

    setLastGame(this.props.gameName);

    this.props.makeGameSession().then(({ data: { makeGameSession } }) => {
      if (!makeGameSession.success) {
        this.props.onError && this.props.onError(makeGameSession.message);
      } else {
        this.setState({
          gameSession: makeGameSession.gameSession,
          sessionStarted: true,
          loading: false
        });

        const { id, vendorId } = makeGameSession.gameSession;

        this.props
          .startGameSession(id, vendorId)
          .then(({ data: { startGameSession } }) => {
            const { success, error } = startGameSession;

            if (!success) {
              this.setState({ startError: error });
            } else {
              this.heartBeatInterval = setInterval(
                this.sendHeartBeat,
                process.env.REACT_APP_GAME_HEARTBEAT_INTERVAL
              );
            }
          })
          .catch(e => this.setState({ startError: e }));
      }
    });

    window.addEventListener('beforeunload', this.handleUnload);
  }

  componentWillUnmount() {
    this.endGameSession(true);
    window.removeEventListener('beforeunload', this.handleUnload);
  }

  handleUnload(e) {
    this.endGameSession();

    // Per spec, the beforeunload event handler should return null to avoid showing a dialog.
    // However, to accommodate IE, it must return undefined instead.
    return undefined;
  }

  endGameSession(synchronous = false) {
    clearInterval(this.heartBeatInterval);

    if (this.state.gameSession) {
      const { gameSession: { id, vendorId } } = this.state;

      // To ensure the end game session call is not cancelled by the browser
      // when this is called from the window.beforeunload event, we need
      // to make the request synchronously. Apollo Client nor Fetch allow
      // making sync calls, so we need to use XMLHttpRequest.
      const token = getToken();
      const request = new XMLHttpRequest();
      request.open('POST', process.env.REACT_APP_GRAPHQL_API_URI, synchronous);
      request.setRequestHeader('authorization', `Bearer ${token}`);
      request.setRequestHeader('content-type', 'application/json');
      request.withCredentials = true; // For maintenance cookie
      request.send(
        JSON.stringify({
          operationName: 'EndGameSession',
          query: MUTATION_END_GAME_SESSION.loc.source.body,
          variables: {
            gameSession: { id, vendorId }
          }
        })
      );
    }
  }

  render() {
    const { gameSession, sessionStarted } = this.state;

    return (
      <div className="game">
        {sessionStarted &&
          gameSession && (
            <iframe
              title="Casino Game"
              className="game-frame"
              src={gameSession.gameUrl}
            />
          )}
      </div>
    );
  }
}

Game.propTypes = {
  gameName: PropTypes.string.isRequired,
  makeGameSession: PropTypes.func.isRequired,
  startGameSession: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  isTournament: PropTypes.bool,
  tournament: tournamentShape
};

const MUTATION_SEND_GAME_SESSION_HEARTBEAT = gql`
  mutation SendGameSessionHeartbeat($gameSession: SendGameSessionHeartbeat!) {
    sendGameSessionHeartbeat(gameSession: $gameSession) {
      status
      message
      timestamp
      error
    }
  }
`;

const MUTATION_MAKE_GAME_SESSION = gql`
  mutation MakeGameSession($gameSession: MakeGameSession!) {
    makeGameSession(gameSession: $gameSession) {
      success
      message
      gameSession {
        id
        gameUrl
        vendorId
      }
    }
  }
`;

const MUTATION_START_GAME_SESSION = gql`
  mutation StartGameSession($gameSession: UpdateGameSession!) {
    startGameSession(gameSession: $gameSession) {
      success
      error
    }
  }
`;

// This mutation is called from within the component using XMLHttpRequest.
const MUTATION_END_GAME_SESSION = gql`
  mutation EndGameSession($gameSession: UpdateGameSession!) {
    endGameSession(gameSession: $gameSession) {
      success
      error
    }
  }
`;

const mapSendGameSessionHeartbeatProps = ({
  ownProps,
  mutateSendGameSessionHeartbeat
}) => ({
  sendGameSessionHeartbeat: gameSession => {
    return mutateSendGameSessionHeartbeat({
      variables: { gameSession: { id: gameSession.id } }
    });
  }
});

const mapMakeGameSessionProps = ({ ownProps, mutateMakeGameSession }) => ({
  makeGameSession: () => {
    const gameSession = { gameName: ownProps.gameName };
    // If this is a tournament, add the tournament id.
    if (ownProps.tournament && ownProps.tournament.id) {
      gameSession.tournamentId = ownProps.tournament.id;
    }
    return mutateMakeGameSession({
      variables: { gameSession }
    });
  }
});

const mapStartGameSessionProps = ({ mutateStartGameSession }) => ({
  startGameSession: (id, vendorId) =>
    mutateStartGameSession({
      variables: { gameSession: { id, vendorId } }
    })
});

export default compose(
  graphql(MUTATION_MAKE_GAME_SESSION, {
    name: 'mutateMakeGameSession',
    props: mapMakeGameSessionProps
  }),
  graphql(MUTATION_START_GAME_SESSION, {
    name: 'mutateStartGameSession',
    props: mapStartGameSessionProps
  }),
  graphql(MUTATION_SEND_GAME_SESSION_HEARTBEAT, {
    name: 'mutateSendGameSessionHeartbeat',
    props: mapSendGameSessionHeartbeatProps
  })
)(Game);



// WEBPACK FOOTER //
// ./src/components/Game/index.js